import React, { useState } from 'react';
import { sendAuthenticatedContactForm } from '../services/contactService';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const TOPICS = [
  'Account question',
  'Health info question',
  'Technical help',
  'Billing',
  'Other',
];

const DashboardContact: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const toggleFaq = (index: number) => {
    setOpenFaq(prev => prev === index ? null : index);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !topic || !message.trim()) {
      setStatus('error');
      setErrorMsg('Please fill in all fields.');
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    try {
      await sendAuthenticatedContactForm({
        name: name.trim(),
        email: email.trim(),
        topic,
        message: message.trim(),
      });
      setStatus('success');
      setName('');
      setEmail('');
      setTopic('');
      setMessage('');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  const faqs = [
    {
      question: 'How do I update my health information?',
      answer: 'Navigate to "Request a Change" from the sidebar. Describe what needs updating and our team will review your request within 2 business days.',
    },
    {
      question: 'Is my health information secure?',
      answer: 'Yes. All data is stored on Canadian servers, protected under PIPEDA and provincial health privacy laws. We use encryption and two-factor authentication to safeguard your information.',
    },
    {
      question: 'How does the Emergency Card work?',
      answer: 'Generate a one-time emergency access code from your dashboard. Print the wallet card and keep it with you. In an emergency, a responder can scan the QR code or enter your Access ID + PIN to view your de-identified health data — no login required.',
    },
    {
      question: 'Can I print my health summary?',
      answer: 'Yes! On the Health Summary page, click "Print Summary" at the top for a clean, printer-friendly version of your complete health record.',
    },
    {
      question: 'What if I need help using the portal?',
      answer: 'Call us at 1-437-313-7554 and our team will walk you through anything you need over the phone.',
    },
  ];

  return (
    <div className="dashboard-contact">
      {/* Header */}
      <div className="dc-header content-card">
        <div className="dc-subtitle">We're here to help.</div>
        <div className="dc-phone">1-437-313-7554</div>
        <div className="dc-hours">Monday to Friday, 9:00 AM – 5:00 PM ET</div>
        <div className="dc-email">support@silverscribe.ca</div>
      </div>

      {/* Grid */}
      <div className="dc-grid">
        {/* Send Us a Message */}
        <div className="content-card">
          <h2>Send Us a Message</h2>

          {status === 'success' && (
            <div className="dc-alert dc-alert-success">
              Your message has been sent. We'll get back to you soon.
            </div>
          )}
          {status === 'error' && errorMsg && (
            <div className="dc-alert dc-alert-error">
              {errorMsg}
            </div>
          )}

          <form className="dc-form" onSubmit={handleSubmit}>
            <div className="dc-field">
              <label className="dc-label">Your Name</label>
              <input
                className="dc-input"
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="dc-field">
              <label className="dc-label">Your Email</label>
              <input
                className="dc-input"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="dc-field">
              <label className="dc-label">What is this about?</label>
              <select
                className="dc-select"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              >
                <option value="">Select a topic...</option>
                {TOPICS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="dc-field">
              <label className="dc-label">Your Message</label>
              <textarea
                className="dc-textarea"
                placeholder="How can we help you?"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>

        {/* FAQ */}
        <div className="content-card">
          <h2>Frequently Asked Questions</h2>
          <div className="dc-faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className="dc-faq-item">
                <button
                  className={`dc-faq-q ${openFaq === i ? 'open' : ''}`}
                  onClick={() => toggleFaq(i)}
                >
                  {faq.question}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                <div className={`dc-faq-a ${openFaq === i ? 'open' : ''}`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .dc-header {
          text-align: center;
          padding: 2rem !important;
        }
        .dc-subtitle {
          font-size: 1.1rem;
          color: var(--text-muted);
        }
        .dc-phone {
          font-family: var(--font-display);
          font-size: 2.25rem;
          color: var(--primary);
          margin: 0.5rem 0;
          letter-spacing: 1px;
        }
        .dc-hours {
          font-size: 0.95rem;
          color: var(--text-muted);
        }
        .dc-email {
          font-size: 0.9rem;
          color: var(--text-light);
          margin-top: 4px;
        }
        .dc-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .dc-field {
          margin-bottom: 16px;
        }
        .dc-label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-muted);
          margin-bottom: 6px;
        }
        .dc-input,
        .dc-select,
        .dc-textarea {
          width: 100%;
          padding: 10px 14px;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text);
          font-size: 0.95rem;
          font-family: inherit;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }
        .dc-input:focus,
        .dc-select:focus,
        .dc-textarea:focus {
          outline: none;
          border-color: var(--primary);
        }
        .dc-input::placeholder,
        .dc-textarea::placeholder {
          color: var(--text-light);
        }
        .dc-select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 36px;
        }
        .dc-textarea {
          resize: vertical;
          min-height: 100px;
        }
        .dc-alert {
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          margin-bottom: 16px;
        }
        .dc-alert-success {
          background: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #a5d6a7;
        }
        .dc-alert-error {
          background: #fbe9e7;
          color: #c62828;
          border: 1px solid #ef9a9a;
        }
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .dc-faq-list {
          display: flex;
          flex-direction: column;
        }
        .dc-faq-item {
          border-bottom: 1px solid var(--border);
        }
        .dc-faq-item:last-child {
          border-bottom: none;
        }
        .dc-faq-q {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 14px 0;
          background: none;
          border: none;
          color: var(--text);
          font-size: 0.95rem;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
          font-family: inherit;
          transition: color 0.2s;
        }
        .dc-faq-q:hover {
          color: var(--primary);
        }
        .dc-faq-q svg {
          flex-shrink: 0;
          transition: transform 0.2s;
          color: var(--text-light);
        }
        .dc-faq-q.open svg {
          transform: rotate(180deg);
        }
        .dc-faq-a {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease, padding 0.3s ease;
          font-size: 0.9rem;
          line-height: 1.6;
          color: var(--text-muted);
        }
        .dc-faq-a.open {
          max-height: 200px;
          padding-bottom: 14px;
        }
        @media (max-width: 768px) {
          .dc-grid {
            grid-template-columns: 1fr;
          }
          .dc-phone {
            font-size: 1.75rem;
          }
          .dc-input,
          .dc-select,
          .dc-textarea {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default DashboardContact;
